import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Mail, Calendar, Shield, LogOut, Lock, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const dateLocale = isRTL ? ar : enUS;
    const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
    const [changingPwd, setChangingPwd] = useState(false);

    const handleChangePassword = async () => {
        if (!pwd.current || !pwd.next) { toast.error(isRTL ? 'أدخل كلمة المرور الحالية والجديدة' : 'Enter current and new password'); return; }
        if (pwd.next !== pwd.confirm) { toast.error(isRTL ? 'تأكيد كلمة المرور غير مطابق' : 'Password confirmation does not match'); return; }
        if (pwd.next.length < 8 || !/[A-Z]/.test(pwd.next) || !/[a-z]/.test(pwd.next) || !/[0-9]/.test(pwd.next)) {
            toast.error(isRTL ? 'كلمة المرور: 8 خانات على الأقل وتحتوي حرفاً كبيراً وصغيراً ورقماً' : 'Min 8 chars with uppercase, lowercase and a number');
            return;
        }
        setChangingPwd(true);
        try {
            await axios.post(`${API}/auth/change-password`,
                { current_password: pwd.current, new_password: pwd.next });
            toast.success(isRTL ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully');
            setPwd({ current: '', next: '', confirm: '' });
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : (isRTL ? 'تعذر تغيير كلمة المرور' : 'Failed to change password'));
        } finally {
            setChangingPwd(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="profile-page">
            <div className="text-center mb-8">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center mx-auto mb-4 shadow-xl shadow-teal-500/25">
                    <span className="text-4xl font-bold text-white">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                </div>
                <h1 className="text-2xl font-bold text-stone-800">{user?.name}</h1>
                <p className="text-stone-500">{user?.email}</p>
            </div>

            <Card className="border-0 shadow-lg shadow-stone-200/50 mb-6">
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <User className="w-5 h-5 text-teal-600" />
                        {t('profile.information')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label className="text-stone-600">{t('profile.fullName')}</Label>
                        <div className="relative">
                            <User className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                            <Input
                                value={user?.name || ''}
                                readOnly
                                className="ps-10 h-12 rounded-xl bg-stone-50 border-stone-200"
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-stone-600">{t('profile.emailAddress')}</Label>
                        <div className="relative">
                            <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                            <Input
                                value={user?.email || ''}
                                readOnly
                                className="ps-10 h-12 rounded-xl bg-stone-50 border-stone-200"
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-stone-600">{t('profile.memberSince')}</Label>
                        <div className="relative">
                            <Calendar className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                            <Input
                                value={user?.created_at ? format(new Date(user.created_at), 'MMMM d, yyyy', { locale: dateLocale }) : ''}
                                readOnly
                                className="ps-10 h-12 rounded-xl bg-stone-50 border-stone-200"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-0 shadow-lg shadow-stone-200/50 mb-6">
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="w-5 h-5 text-teal-600" />
                        {t('profile.privacySecurity')}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="p-4 bg-teal-50 rounded-xl border border-teal-100">
                            <h4 className="font-medium text-teal-800 mb-1">{t('profile.dataProtection')}</h4>
                            <p className="text-sm text-teal-700">
                                {t('profile.dataProtectionDesc')}
                            </p>
                        </div>
                        <div className="p-4 bg-stone-50 rounded-xl">
                            <h4 className="font-medium text-stone-800 mb-1">{t('profile.dataRetention')}</h4>
                            <p className="text-sm text-stone-600">
                                {t('profile.dataRetentionDesc')}
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Change Password */}
            <Card className="border-0 shadow-lg shadow-stone-200/50 mb-6" data-testid="change-password-card">
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Lock className="w-5 h-5 text-teal-600" />
                        {isRTL ? 'تغيير كلمة المرور' : 'Change Password'}
                    </CardTitle>
                    <p className="text-xs text-stone-500">
                        {isRTL ? '8 خانات على الأقل وتحتوي حرفاً كبيراً وحرفاً صغيراً ورقماً' : 'Min 8 characters with uppercase, lowercase and a number'}
                    </p>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label className="text-stone-600">{isRTL ? 'كلمة المرور الحالية' : 'Current password'}</Label>
                        <Input type="password" dir="ltr" value={pwd.current}
                            onChange={(e) => setPwd(p => ({ ...p, current: e.target.value }))}
                            className="h-12 rounded-xl" data-testid="current-password-input" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-stone-600">{isRTL ? 'كلمة المرور الجديدة' : 'New password'}</Label>
                            <Input type="password" dir="ltr" value={pwd.next}
                                onChange={(e) => setPwd(p => ({ ...p, next: e.target.value }))}
                                className="h-12 rounded-xl" data-testid="new-password-input" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-stone-600">{isRTL ? 'تأكيد الجديدة' : 'Confirm new'}</Label>
                            <Input type="password" dir="ltr" value={pwd.confirm}
                                onChange={(e) => setPwd(p => ({ ...p, confirm: e.target.value }))}
                                className="h-12 rounded-xl" data-testid="confirm-password-input" />
                        </div>
                    </div>
                    <Button onClick={handleChangePassword} disabled={changingPwd}
                        className="bg-teal-500 hover:bg-teal-600 rounded-xl" data-testid="change-password-btn">
                        {changingPwd ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Lock className="w-4 h-4 me-2" />}
                        {isRTL ? 'تغيير كلمة المرور' : 'Change Password'}
                    </Button>
                </CardContent>
            </Card>

            <Button
                onClick={handleLogout}
                variant="outline"
                className="w-full h-12 rounded-xl text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                data-testid="logout-btn"
            >
                <LogOut className="w-5 h-5 me-2" />
                {t('profile.signOut')}
            </Button>
        </div>
    );
};

export default ProfilePage;
